import { useBatchExpiration, useFlagBatchRecall } from '@hooks/useBatchTracking'

const statusColors: Record<string, string> = {
  FRESH: 'badge-success',
  NEARING_EXPIRY: 'badge-warning',
  EXPIRING_SOON: 'badge-danger',
  EXPIRED: 'badge-error',
  NO_EXPIRY: 'badge-info',
}

const statusIcons: Record<string, string> = {
  FRESH: '✓',
  NEARING_EXPIRY: '⚠',
  EXPIRING_SOON: '🔴',
  EXPIRED: '❌',
  NO_EXPIRY: 'ℹ',
}

export function BatchExpirationPage() {
  const { data, isLoading, error } = useBatchExpiration()
  const flagRecall = useFlagBatchRecall()

  const batches = data?.data || []

  const handleRecall = (batchId: number, productName: string) => {
    const reason = prompt(
      `Recall reason for ${productName}:\n\n(e.g., Quality issue, Expiration)`
    )
    if (reason) {
      flagRecall.mutate({
        batchId,
        reason,
        severity: 'HIGH',
      })
    }
  }

  const freshCount = batches.filter((b: any) => b.expiryStatus === 'FRESH').length
  const nearingCount = batches.filter((b: any) => b.expiryStatus === 'NEARING_EXPIRY').length
  const expiringCount = batches.filter((b: any) => b.expiryStatus === 'EXPIRING_SOON').length
  const expiredCount = batches.filter((b: any) => b.expiryStatus === 'EXPIRED').length

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Batch Expiration Tracking</h1>
        <p className="text-gray-600 text-sm mt-1">
          Monitor expiration status and manage recalls
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-green-500">
          <div className="text-xs text-green-600 font-semibold uppercase">Fresh</div>
          <div className="text-2xl font-bold text-green-900 mt-1">{freshCount}</div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100 border-l-4 border-yellow-500">
          <div className="text-xs text-yellow-600 font-semibold uppercase">Nearing</div>
          <div className="text-2xl font-bold text-yellow-900 mt-1">{nearingCount}</div>
        </div>

        <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-l-4 border-orange-500">
          <div className="text-xs text-orange-600 font-semibold uppercase">Expiring Soon</div>
          <div className="text-2xl font-bold text-orange-900 mt-1">{expiringCount}</div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-red-100 border-l-4 border-red-500">
          <div className="text-xs text-red-600 font-semibold uppercase">Expired</div>
          <div className="text-2xl font-bold text-red-900 mt-1">{expiredCount}</div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          Failed to load batch data. Please try again.
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading batches...</div>
      ) : batches.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No batches found.</div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-semibold">Batch No.</th>
                  <th className="text-left px-4 py-2 font-semibold">Product</th>
                  <th className="text-right px-4 py-2 font-semibold">Available</th>
                  <th className="text-left px-4 py-2 font-semibold">Received Date</th>
                  <th className="text-left px-4 py-2 font-semibold">Expiry Date</th>
                  <th className="text-center px-4 py-2 font-semibold">Days Left</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch: any) => (
                  <tr
                    key={batch.id}
                    className={`border-b hover:bg-gray-50 transition ${
                      batch.expiryStatus === 'EXPIRED' ? 'bg-red-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono font-medium">
                      {batch.batchNo}
                    </td>
                    <td className="px-4 py-3">{batch.product.name}</td>
                    <td className="px-4 py-3 text-right">
                      {batch.availableQtyForRepack.toFixed(2)} {batch.product.baseUom}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(batch.receivedDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {batch.expiryDate
                        ? new Date(batch.expiryDate).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center font-medium">
                      <span
                        className={
                          batch.daysUntilExpiry !== null && batch.daysUntilExpiry < 0
                            ? 'text-red-600'
                            : batch.daysUntilExpiry !== null && batch.daysUntilExpiry < 7
                            ? 'text-orange-600'
                            : 'text-green-600'
                        }
                      >
                        {batch.daysUntilExpiry !== null
                          ? batch.daysUntilExpiry > 0
                            ? `${batch.daysUntilExpiry}d`
                            : 'Expired'
                          : '∞'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`badge ${
                          statusColors[batch.expiryStatus]
                        }`}
                      >
                        {statusIcons[batch.expiryStatus]} {batch.expiryStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(batch.expiryStatus === 'EXPIRED' ||
                        batch.expiryStatus === 'EXPIRING_SOON') && (
                        <button
                          onClick={() =>
                            handleRecall(batch.id, batch.product.name)
                          }
                          disabled={flagRecall.isPending}
                          className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                        >
                          Recall
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
